import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_LOW, KEY_TAB_COMMAND } from 'lexical';
import React, { useCallback, useEffect } from 'react';

interface TabCyclePluginProps {
    cancelButtonRef: React.RefObject<HTMLButtonElement>;
    saveButtonRef: React.RefObject<HTMLButtonElement>;
}

export function TabCyclePlugin({ cancelButtonRef, saveButtonRef }: TabCyclePluginProps) {
    const [editor] = useLexicalComposerContext();

    const handleCancelKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                editor.focus();
            }
        },
        [editor]
    );

    const handleSaveKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                editor.focus();
            }
        },
        [editor]
    );

    const handleTabCommand = useCallback(
        (event: KeyboardEvent | null) => {
            event?.preventDefault();
            if (event?.shiftKey) {
                saveButtonRef.current?.focus();
            } else {
                cancelButtonRef.current?.focus();
            }
            return true;
        },
        [cancelButtonRef, saveButtonRef]
    );

    useEffect(() => {
        const cancelEl = cancelButtonRef.current;
        const saveEl = saveButtonRef.current;

        const removeTabCommand = editor.registerCommand(
            KEY_TAB_COMMAND,
            handleTabCommand,
            COMMAND_PRIORITY_LOW
        );

        cancelEl?.addEventListener('keydown', handleCancelKeyDown);
        saveEl?.addEventListener('keydown', handleSaveKeyDown);

        return () => {
            removeTabCommand();
            cancelEl?.removeEventListener('keydown', handleCancelKeyDown);
            saveEl?.removeEventListener('keydown', handleSaveKeyDown);
        };
    }, [
        editor,
        cancelButtonRef,
        saveButtonRef,
        handleCancelKeyDown,
        handleSaveKeyDown,
        handleTabCommand,
    ]);

    return null;
}
